import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ESG_CONTENT_SHAPE } from '../../constants/colors';
import { contentBorders } from '../../theme/createContentTheme';

type Columns = { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };

export interface MetricStripProps {
  children: React.ReactNode;
  /** Columns per breakpoint (default 1 / 2 / 4). */
  columns?: Columns;
  /** `panel` — bordered paper panel; `plain` — hairlines only, on the canvas. */
  variant?: 'panel' | 'plain';
  dense?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Row of secondary figures separated by hairlines instead of a grid of cards.
 * Separators come from each cell's start/top border; the grid is shifted by
 * -1px and clipped so the outer edge never shows a stray line, whatever the
 * column count or the number of items in the last row.
 */
const MetricStrip: React.FC<MetricStripProps> = ({
  children,
  columns = { xs: 1, sm: 2, md: 4 },
  variant = 'panel',
  dense = false,
  sx,
}) => {
  const template = Object.fromEntries(
    Object.entries(columns).map(([bp, n]) => [bp, `repeat(${n}, minmax(0, 1fr))`]),
  );
  const items = React.Children.toArray(children).filter(Boolean);

  return (
    <Box
      sx={[
        (theme) => ({
          overflow: 'hidden',
          minWidth: 0,
          ...(variant === 'panel'
            ? {
                bgcolor: 'background.paper',
                border: `1px solid ${contentBorders(theme).default}`,
                borderRadius: `${ESG_CONTENT_SHAPE.md}px`,
              }
            : {
                borderTop: `1px solid ${contentBorders(theme).default}`,
                borderBottom: `1px solid ${contentBorders(theme).default}`,
              }),
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: template,
          marginInlineStart: '-1px',
          marginTop: '-1px',
        }}
      >
        {items.map((child, i) => (
          <Box
            key={i}
            sx={(theme) => ({
              minWidth: 0,
              px: dense ? 2 : { xs: 1.5, md: 2.5 },
              py: dense ? 1.75 : { xs: 2, md: 2.5 },
              borderInlineStart: `1px solid ${contentBorders(theme).subtle}`,
              borderTop: `1px solid ${contentBorders(theme).subtle}`,
            })}
          >
            {child}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MetricStrip;
